import { useMemo, useRef } from 'react';
import { z } from 'zod';
import { rpcClient } from 'src/api/rpc/client';
import { stopAndAwaitTurn, stopTurnOnServer } from 'src/context/liveTurn';
import type { FrontendTool, ModuleKey, PageKey } from '@erp/rpc';
import type {
  WizardAgentAdapter,
  WizardAgentChannel,
  WizardProposalMeta,
} from '@erp/ui/hooks/use-wizard';

/**
 * Shared adapter bridging the generic wizard's {@link WizardAgentChannel} to
 * the ERP chat agent (the `wizard` module). It is the one place that knows how
 * "agentic help" is implemented; the @erp/ui wizard stays ignorant of oRPC and
 * the LLM.
 *
 * The agent acts only through the affordances a human has, exactly as the
 * wizard's contract requires:
 *   - `wizard_propose_values` (fire-and-forget) -> channel.proposeValues, shown
 *     as inline accept/reject suggestions.
 *   - `wizard_flag_issue` (fire-and-forget) -> channel.flagIssue.
 *   - `wizard_advance` (awaitable) -> channel.advance(); only offered while
 *     autopilot is running. Gate confirmation is enforced by the wizard hook,
 *     so advancing a gate step blocks on the human's confirmation and the
 *     awaitable round-trip carries the outcome back to the agent.
 *
 * Usage:
 * ```tsx
 * const agent = useChatAgentWizardAdapter({ currentPage: '/things/new' });
 * const wizard = useWizard({ agent, steps, onComplete });
 * return (
 *   <Wizard api={wizard}>
 *     <WizardRail />
 *     <WizardSurface>…<WizardFooter /></WizardSurface>
 *     <WizardAssist />
 *   </Wizard>
 * );
 * ```
 */

export interface ChatAgentWizardAdapterOptions {
  /** Registered chat-agent module key. Defaults to `"wizard"`. */
  moduleKey?: ModuleKey;
  /** Optional page key for page-specific prompt/tools. */
  pageKey?: PageKey;
  /** Current route path, passed to the agent for context/telemetry. */
  currentPage: string;
  /** Name shown in the assist panel header. Defaults to `"Assistant"`. */
  name?: string;
  /** Whether autopilot is offered. Defaults to `true`. */
  supportsAutopilot?: boolean;
  /** Extra fields merged into `moduleContext` each turn (domain hints). */
  getExtraContext?: () => Record<string, unknown> | undefined;
}

const proposeValuesParams = z.object({
  values: z
    .record(z.string(), z.unknown())
    .describe(
      'Field key to proposed value, for the current step. Include only fields you can fill confidently.',
    ),
  confidence: z.number().min(0).max(1).optional(),
  rationale: z.string().optional(),
});

const flagIssueParams = z.object({
  message: z.string(),
  severity: z.enum(['error', 'warning']).optional(),
  field: z.string().optional(),
});

const advanceParams = z.object({});

/** Type guard so a non-Error rejection (null/undefined/etc.) can't throw. */
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

function frontendTool(
  name: string,
  description: string,
  params: z.ZodType,
  awaitable = false,
): FrontendTool {
  return {
    name,
    description,
    parameters: z.toJSONSchema(params) as Record<string, unknown>,
    awaitable,
  };
}

const PROPOSE_TOOL = frontendTool(
  'wizard_propose_values',
  'Suggest values for one or more fields on the current wizard step. The user reviews each suggestion inline and accepts or dismisses it.',
  proposeValuesParams,
);
const FLAG_TOOL = frontendTool(
  'wizard_flag_issue',
  'Flag a specific problem with the current step (a mismatch, a likely typo, a contradictory value).',
  flagIssueParams,
);
const ADVANCE_TOOL = frontendTool(
  'wizard_advance',
  'Validate the current step and move to the next one. A gate step will require the user to confirm before it commits.',
  advanceParams,
  true,
);

/** Manual help may only propose and flag; advancing is the human's click. */
const MANUAL_TOOLS: FrontendTool[] = [PROPOSE_TOOL, FLAG_TOOL];
/** Autopilot additionally may advance (gates still stop for the human). */
const AUTOPILOT_TOOLS: FrontendTool[] = [PROPOSE_TOOL, FLAG_TOOL, ADVANCE_TOOL];

export function createChatAgentWizardAdapter(
  options: ChatAgentWizardAdapterOptions,
): WizardAgentAdapter {
  const moduleKey: ModuleKey = options.moduleKey ?? 'wizard';

  let channel: WizardAgentChannel | null = null;
  let threadId: string | undefined;
  // Monotonic run token: any in-flight turn checks it and bails if a newer
  // trigger (new step, ask, pause, teardown) has superseded it.
  let runId = 0;
  // Set only while a turn's stream is open.
  let currentAbort: AbortController | null = null;

  // The server run behind the open stream, from `thread-info`.
  let currentRunId: string | undefined;

  /**
   * Pause/teardown: the server turn outlives the stream, so stop it too. A
   * stream closed before `thread-info` stops its turn on the server anyway.
   */
  function stopCurrentTurn(): void {
    if (currentAbort && threadId && currentRunId) {
      void stopTurnOnServer(threadId, currentRunId);
    }
    currentAbort?.abort();
  }

  function buildModuleContext(ch: WizardAgentChannel): Record<string, unknown> {
    // Extra context is merged first so the wizard's own snapshot fields stay
    // authoritative and can't be clobbered by a colliding key.
    const extraContext = options.getExtraContext?.() ?? {};
    const snapshot = ch.getSnapshot();
    const activeStep = snapshot.activeStep;
    return {
      ...extraContext,
      activeStepId: snapshot.activeStepId,
      activeStep: {
        id: activeStep.id,
        title: activeStep.title,
        description: activeStep.description,
        agentBrief: activeStep.agentBrief,
        gate: activeStep.gate,
      },
      steps: snapshot.steps.map((step) => ({
        id: step.id,
        title: step.title,
        gate: step.gate,
        optional: step.optional,
        status: snapshot.statuses[step.id],
      })),
      values: snapshot.values,
    };
  }

  // fallow-ignore-next-line complexity -- pre-existing turn loop; AI-520 only clears the in-flight marker in finally.
  async function runTurn(
    myRun: number,
    userText: string,
    tools: FrontendTool[],
  ): Promise<void> {
    const ch = channel;
    if (!ch) return;

    // A newer trigger replaces the turn before it. The server would queue
    // this message behind a turn still running, so stop that one and let it
    // end first (AI-518).
    const previousRunId = currentRunId;
    currentAbort?.abort();
    currentRunId = undefined;
    ch.setActivity('thinking');
    if (threadId && previousRunId) {
      await stopAndAwaitTurn(threadId, previousRunId);
      if (myRun !== runId) return;
    }
    const abort = new AbortController();
    currentAbort = abort;

    const advanceEnabled = tools.some((t) => t.name === ADVANCE_TOOL.name);
    let assistantText = '';

    try {
      const iterator = await rpcClient.ai.chatAgent.chat(
        {
          moduleKey,
          pageKey: options.pageKey,
          currentPage: options.currentPage,
          threadId,
          requestId: crypto.randomUUID(),
          // The server rebuilds earlier turns from the thread.
          message: { content: userText },
          // TODO(AI-514 follow-up): drop once every backend reads `message`;
          // servers before #8194 still require `messages`.
          messages: [{ role: 'user', content: userText }],
          moduleContext: buildModuleContext(ch),
          frontendTools: tools,
        },
        { signal: abort.signal },
      );

      for await (const event of iterator) {
        if (myRun !== runId) break;

        switch (event.type) {
          case 'text-delta':
            assistantText += event.text;
            break;

          case 'thread-info':
            threadId = event.threadId;
            currentRunId = event.runId;
            break;

          case 'submission':
            // Still behind a turn that didn't end in time: take the message
            // back rather than have it answered out of sight.
            if (event.status === 'queued' && event.threadId) {
              void rpcClient.ai.chatAgent
                .withdrawSubmission({
                  threadId: event.threadId,
                  requestId: event.requestId,
                })
                .catch(() => undefined);
              ch.say('Still wrapping up the last step; ask again in a moment.');
            }
            break;

          case 'tool-call':
            // Awaitable tool: run now and round-trip the result so the agent
            // sees it in the same turn (the backend blocks until we POST it).
            if (event.toolName === ADVANCE_TOOL.name && advanceEnabled) {
              const { toolCallId } = event;
              void (async () => {
                let advanced = false;
                try {
                  advanced = await ch.advance();
                } catch {
                  advanced = false;
                }
                try {
                  await rpcClient.ai.chatAgent.submitFrontendToolResult({
                    toolCallId,
                    result: { advanced },
                  });
                } catch (err) {
                  console.error('wizard_advance result submit failed', err);
                }
              })();
            }
            break;

          case 'tool-result':
            // Fire-and-forget tools deliver their args as the result.
            if (event.toolName === PROPOSE_TOOL.name) {
              const parsed = proposeValuesParams.safeParse(event.result);
              if (parsed.success) {
                const meta: WizardProposalMeta = {
                  confidence: parsed.data.confidence,
                  rationale: parsed.data.rationale,
                };
                ch.proposeValues(
                  ch.getSnapshot().activeStepId,
                  parsed.data.values,
                  meta,
                );
              }
            } else if (event.toolName === FLAG_TOOL.name) {
              const parsed = flagIssueParams.safeParse(event.result);
              if (parsed.success) {
                ch.flagIssue({
                  stepId: ch.getSnapshot().activeStepId,
                  message: parsed.data.message,
                  severity: parsed.data.severity ?? 'warning',
                  field: parsed.data.field,
                });
              }
            }
            break;

          case 'error':
            ch.say(`Sorry, something went wrong: ${event.message}`);
            break;
        }
      }

      if (myRun === runId && assistantText.trim()) {
        ch.say(assistantText.trim());
      }
    } catch (err) {
      if (myRun === runId && !isAbortError(err)) {
        ch.say("I couldn't reach the assistant just now.");
      }
    } finally {
      if (currentAbort === abort) currentAbort = null;
      if (myRun === runId) ch.setActivity('idle');
    }
  }

  // fallow-ignore-next-line complexity -- pre-existing autopilot loop, unchanged by AI-520; flagged because its file changed.
  async function runAutopilot(
    myRun: number,
    ch: WizardAgentChannel,
  ): Promise<void> {
    for (;;) {
      const before = ch.getSnapshot();
      if (myRun !== runId || before.isComplete || !before.autopilotRunning) {
        return;
      }
      await runTurn(
        myRun,
        `Continue the "${before.activeStep.title}" step: propose any remaining values you're confident about, then advance.`,
        AUTOPILOT_TOOLS,
      );
      if (myRun !== runId) return;

      const after = ch.getSnapshot();
      if (after.isComplete) return;
      if (after.activeStepId === before.activeStepId) {
        // The turn didn't move the wizard on; stop rather than loop tightly.
        ch.say(
          "I've paused here. Adjust a field or tell me how to proceed and I'll continue.",
        );
        return;
      }
    }
  }

  return {
    name: options.name ?? 'Assistant',
    supportsAutopilot: options.supportsAutopilot ?? true,
    connect(ch) {
      channel = ch;
      return () => {
        runId += 1;
        stopCurrentTurn();
        channel = null;
      };
    },
    onStepEnter(_stepId, ch) {
      // Autopilot drives its own per-step turns; don't double up.
      if (ch.getSnapshot().autopilotRunning) return;
      const myRun = ++runId;
      const title = ch.getSnapshot().activeStep.title;
      void runTurn(
        myRun,
        `I just opened the "${title}" step. If you can confidently pre-fill any of its fields from the available context, propose them now. Keep it to one short sentence.`,
        MANUAL_TOOLS,
      );
    },
    onAsk(text) {
      const myRun = ++runId;
      void runTurn(myRun, text, MANUAL_TOOLS);
    },
    onStartAutopilot(ch) {
      const myRun = ++runId;
      void runAutopilot(myRun, ch);
    },
    onPauseAutopilot() {
      runId += 1;
      stopCurrentTurn();
    },
  };
}

/**
 * React wrapper that memoizes the adapter so the wizard connects once. Dynamic
 * inputs (`getExtraContext`) are read through a ref, so they never force a
 * reconnect.
 */
export function useChatAgentWizardAdapter(
  options: ChatAgentWizardAdapterOptions,
): WizardAgentAdapter {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  return useMemo(
    () =>
      createChatAgentWizardAdapter({
        moduleKey: options.moduleKey,
        pageKey: options.pageKey,
        currentPage: options.currentPage,
        name: options.name,
        supportsAutopilot: options.supportsAutopilot,
        getExtraContext: () => optionsRef.current.getExtraContext?.(),
      }),
    [
      options.moduleKey,
      options.pageKey,
      options.currentPage,
      options.name,
      options.supportsAutopilot,
    ],
  );
}
